import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';
import ViewToggle from '../components/ViewToggle';
import { formatDate } from '../utils/date';
import { cardHeaderHue } from '../utils/cardTheme';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 'All'];
const SORT_COLUMNS = [
  { key: 'name', label: 'Scheme Name' },
  { key: 'component_count', label: 'Components' },
  { key: 'updated_at', label: 'Updated' },
  { key: 'is_active', label: 'Status' },
];

const PCT_RE = /^\d+(\.\d)?$/;

function compareValues(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) result.push('…');
    result.push(p);
    prev = p;
  }
  return result;
}

const EMPTY_COMPONENT = () => ({ id: null, name: '', percentage: '', is_active: 1, children: [] });
const EMPTY_CHILD = () => ({ id: null, name: '', percentage: '', is_active: 1 });

// Turns the flat parent+child row list the API returns into the nested
// component/children shape the builder form edits.
function nestComponents(flat) {
  const parents = flat.filter((c) => !c.parent_id).sort((a, b) => a.sort_order - b.sort_order);
  return parents.map((p) => ({
    id: p.id,
    name: p.name,
    percentage: String(Math.round(Number(p.percentage) || 0)),
    is_active: p.is_active ? 1 : 0,
    children: flat
      .filter((c) => c.parent_id === p.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((ch) => ({ id: ch.id, name: ch.name, percentage: String(Math.round(Number(ch.percentage) || 0)), is_active: ch.is_active ? 1 : 0 })),
  }));
}

function validateComponents(components) {
  let total = 0;
  for (const c of components) {
    if (!c.name || !c.name.trim()) return 'Every component needs a name';
    if (c.percentage === '' || c.percentage === null || c.percentage === undefined || Number.isNaN(Number(c.percentage))) {
      return `Percentage is required for ${c.name}`;
    }
    if (!PCT_RE.test(String(c.percentage).trim())) {
      return `"${c.name}" weight must be a whole number or have at most 1 decimal place`;
    }
    total += Number(c.percentage);
    for (const child of (c.children || [])) {
      if (!child.name || !child.name.trim()) return 'Every sub-category needs a name';
      if (child.percentage === '' || child.percentage === null || child.percentage === undefined || Number.isNaN(Number(child.percentage))) {
        return `Percentage is required for ${child.name}`;
      }
      if (!PCT_RE.test(String(child.percentage).trim())) {
        return `"${child.name}" weight must be a whole number or have at most 1 decimal place`;
      }
    }
  }
  if (Math.round(total * 10) / 10 !== 100) {
    return `Component weights must total 100% (currently ${total}%)`;
  }
  return null;
}

export default function ComponentMasterPage() {
  const { show } = useToast();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [view, setView] = useState('tiles');

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [schemeId, setSchemeId] = useState(null);
  const [schemeName, setSchemeName] = useState('');
  const [components, setComponents] = useState([]);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/records/components/list'),
      api.get('/users/me/permissions'),
    ])
      .then(([schemesRes, permsRes]) => {
        setSchemes(schemesRes.data.data);
        setPerms(permsRes.data.data.component_master || { view: false, create: false, update: false, delete: false });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load component schemes'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, pageSize]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa-solid fa-sort" style={{ color: 'var(--slate-lt)', marginLeft: '.35rem' }} />;
    return <i className={`fa-solid ${sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} style={{ color: 'var(--ocean)', marginLeft: '.35rem' }} />;
  };

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = schemes.filter((s) => !term || s.name.toLowerCase().includes(term));
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      list = list.slice().sort((a, b) => compareValues(a[sortKey], b[sortKey]) * dir);
    }
    return list;
  }, [schemes, search, sortKey, sortDir]);

  const total = filteredSorted.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return filteredSorted;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredSorted.slice(start, start + effectivePageSize);
  }, [filteredSorted, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const totalWeight = useMemo(() => components.reduce((sum, c) => sum + (Number(c.percentage) || 0), 0), [components]);

  const openCreate = () => {
    setSchemeId(null);
    setSchemeName('');
    setComponents([EMPTY_COMPONENT()]);
    setFormError(null);
    setModalMode('create');
  };

  const openEdit = async (row) => {
    setModalMode('edit');
    setModalLoading(true);
    setFormError(null);
    try {
      const res = await api.get('/records/components', { params: { id: row.id } });
      const data = res.data.data;
      setSchemeId(data.id);
      setSchemeName(data.name);
      setComponents(nestComponents(data.components));
    } catch (err) {
      show(err.response?.data?.message || 'Failed to load scheme', 'error');
      setModalMode(null);
    } finally {
      setModalLoading(false);
    }
  };

  const openView = async (row) => {
    setViewLoading(true);
    try {
      const res = await api.get('/records/components', { params: { id: row.id } });
      setViewData(res.data.data);
    } catch (err) {
      show(err.response?.data?.message || 'Failed to load scheme', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setSchemeId(null);
    setSchemeName('');
    setComponents([]);
    setFormError(null);
  };

  const addComponent = () => setComponents((list) => [...list, EMPTY_COMPONENT()]);
  const removeComponent = (idx) => setComponents((list) => list.filter((_, i) => i !== idx));
  const updateComponent = (idx, field, value) => setComponents((list) => list.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const addChild = (idx) => setComponents((list) => list.map((c, i) => (i === idx ? { ...c, children: [...c.children, EMPTY_CHILD()] } : c)));
  const removeChild = (idx, childIdx) => setComponents((list) => list.map((c, i) => (i === idx ? { ...c, children: c.children.filter((_, j) => j !== childIdx) } : c)));
  const updateChild = (idx, childIdx, field, value) => setComponents((list) => list.map((c, i) => (
    i === idx ? { ...c, children: c.children.map((ch, j) => (j === childIdx ? { ...ch, [field]: value } : ch)) } : c
  )));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schemeName.trim()) {
      setFormError('Scheme name is required.');
      return;
    }
    if (!components.length) {
      setFormError('At least one component is required.');
      return;
    }
    const validationError = validateComponents(components);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    const payload = {
      id: schemeId,
      name: schemeName.trim(),
      components: components.map((c) => ({
        id: c.id,
        name: c.name.trim(),
        percentage: Number(c.percentage),
        is_active: c.is_active ? 1 : 0,
        children: (c.children || []).map((ch) => ({
          id: ch.id, name: ch.name.trim(), percentage: Number(ch.percentage), is_active: ch.is_active ? 1 : 0,
        })),
      })),
    };
    try {
      await api.put('/records/components', payload);
      show(modalMode === 'create' ? 'Scheme created' : 'Scheme updated', 'success');
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} scheme`);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/records/components/${row.id}/active`, { is_active: row.is_active ? 0 : 1 });
      show(`Scheme ${row.is_active ? 'deactivated' : 'activated'}`, 'success');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update scheme status', 'error');
    }
  };

  const confirmDelete = async () => {
    const row = deleteTarget;
    setDeleting(true);
    try {
      await api.delete('/records/components', { params: { id: row.id } });
      show('Scheme deleted', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to delete scheme', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => setSearch('');

  if (!loading && !error && perms && !perms.view) {
    return (
      <div>
        <div className="sh-ey">PROGRAMMES · COMPONENT MASTER</div>
        <div className="sh-ti">Component Master</div>
        <div className="alert"><i className="fa-solid fa-lock" /><div className="alert-t">You do not have access to Component Master.</div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sh-ey">PROGRAMMES · COMPONENT MASTER</div>
      <div className="sh-ti">Component Master</div>
      <div className="sh-de">Marking schemes and their weighted components — used to score modules. A scheme locks permanently once marks have been recorded against it.</div>

      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 240px', minWidth: 200 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search scheme name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {view === 'tiles' && (
          <div className="icon-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
            <i className="fa-solid fa-arrow-down-a-z" />
            <SearchableSelect
              className="finput" value={sortKey || ''} onChange={(val) => handleSort(val || null)}
              placeholder="Default order"
              options={[{ value: '', label: 'Default order' }, ...SORT_COLUMNS.map((o) => ({ value: o.key, label: `Sort: ${o.label}` }))]}
            />
          </div>
        )}
        {view === 'tiles' && sortKey && (
          <button type="button" className="btn btn-outline btn-toolbar" title={sortDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short'}`} />
          </button>
        )}
        <button type="button" className="btn btn-outline btn-toolbar" onClick={clearFilters}><i className="fa-solid fa-arrow-rotate-left" /> Clear</button>
        <ViewToggle view={view} onChange={setView} />
        {perms?.create && (
          <button className="btn btn-primary btn-toolbar" style={{ marginLeft: 'auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Scheme</button>
        )}
      </div>

      {loading && <Loader label="Loading component schemes…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !schemes.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-chart-pie" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No marking schemes yet</div>
          <div style={{ color: 'var(--slate)', marginBottom: '1rem' }}>Add your first scheme to get started.</div>
          {perms?.create && (
            <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Scheme</button>
          )}
        </div>
      )}

      {!loading && !error && !!schemes.length && !total && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No schemes match the current search.
        </div>
      )}

      {!loading && !error && !!total && (
        <div>
          {view === 'tiles' && (
            <div className="g4 view-fade" key="tiles">
              {pageRows.map((row) => (
                <div key={row.id} className="cc-card" style={{ cursor: 'pointer' }} onClick={() => openView(row)}>
                  <div className="cc-hd cc-hd-tint" style={{ '--h': cardHeaderHue(row.name) }}>
                    <div className="cc-em"><i className="fa-solid fa-chart-pie" /></div>
                    <div style={{ minWidth: 0 }}>
                      <div className="cc-ti">{row.name}</div>
                      {row.locked && <span className="pill p-lock" style={{ marginTop: '.3rem', display: 'inline-flex' }}><i className="fa-solid fa-lock" /> Locked</span>}
                    </div>
                  </div>
                  <div className="cc-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
                      <i className="fa-solid fa-layer-group" style={{ color: 'var(--slate-lt)' }} /> Components <strong style={{ color: 'var(--ink-s)' }}>{row.component_count}</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: 'var(--text-sm)', color: 'var(--slate)', marginTop: '.4rem' }}>
                      <i className="fa-solid fa-clock" style={{ color: 'var(--slate-lt)' }} /> Updated <strong style={{ color: 'var(--ink-s)' }}>{formatDate(row.updated_at)}</strong>
                    </div>
                  </div>
                  <div className="cc-foot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={(e) => e.stopPropagation()}>
                      {perms?.update ? (
                        <div className={`toggle ${row.is_active ? 'on' : ''}`} role="switch" aria-checked={!!row.is_active} onClick={() => toggleActive(row)} title={row.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'} />
                      ) : null}
                      <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${row.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {row.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem' }} onClick={(e) => e.stopPropagation()}>
                      {perms?.update && (
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={row.locked}
                          title={row.locked ? 'Locked — has recorded marks, structure cannot change' : 'Edit'}
                          onClick={() => openEdit(row)}
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                      )}
                      {perms?.delete && (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={row.locked}
                          title={row.locked ? 'Locked — has recorded marks, cannot be deleted' : 'Delete'}
                          onClick={() => setDeleteTarget(row)}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === 'table' && (
            <div className="card view-fade" key="table">
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>#</th>
                      {SORT_COLUMNS.map(({ key, label }) => (
                        <th key={key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
                          {label}{sortIcon(key)}
                        </th>
                      ))}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, i) => (
                      <tr key={row.id}>
                        <td>{rangeStart + i}</td>
                        <td style={{ fontWeight: 600 }}>
                          {row.name}
                          {row.locked && <span className="pill p-lock" style={{ marginLeft: '.5rem' }}><i className="fa-solid fa-lock" /> Locked</span>}
                        </td>
                        <td>{row.component_count}</td>
                        <td>{formatDate(row.updated_at)}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                            {perms?.update ? (
                              <div className={`toggle ${row.is_active ? 'on' : ''}`} role="switch" aria-checked={!!row.is_active} onClick={() => toggleActive(row)} title={row.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'} />
                            ) : null}
                            <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${row.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {row.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td>
                          <button className="btn btn-outline btn-sm" style={{ marginRight: '.4rem' }} onClick={() => openView(row)}><i className="fa-solid fa-eye" /></button>
                          {perms?.update && (
                            <button
                              className="btn btn-outline btn-sm"
                              style={{ marginRight: '.4rem' }}
                              disabled={row.locked}
                              title={row.locked ? 'Locked — has recorded marks, structure cannot change' : 'Edit'}
                              onClick={() => openEdit(row)}
                            >
                              <i className="fa-solid fa-pen" />
                            </button>
                          )}
                          {perms?.delete && (
                            <button
                              className="btn btn-danger btn-sm"
                              disabled={row.locked}
                              title={row.locked ? 'Locked — has recorded marks, cannot be deleted' : 'Delete'}
                              onClick={() => setDeleteTarget(row)}
                            >
                              <i className="fa-solid fa-trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
              Showing {rangeStart}–{rangeEnd} of {total}
            </div>
            <div className="icon-field" style={{ marginLeft: 'auto' }}>
              <SearchableSelect
                className="finput" style={{ width: 110 }} value={pageSize}
                onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
                options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
              />
            </div>
            {pageSize !== 'All' && totalPages > 1 && (
              <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
                {buildPageList(currentPage, totalPages).map((p, idx) => (
                  p === '…'
                    ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                    : (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    )
                ))}
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {modalMode && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 620, maxWidth: '94vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modalMode === 'create' ? 'Add Marking Scheme' : 'Edit Marking Scheme'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
            {modalLoading && <Loader label="Loading scheme…" />}
            {!modalLoading && (
              <>
                {formError && <div className="lc-err">{formError}</div>}
                <div className="fg fg-tight"><label className="fl">Scheme Name *</label><input className="finput" required value={schemeName} onChange={(e) => setSchemeName(e.target.value)} /></div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.6rem' }}>
                  <label className="fl" style={{ margin: 0 }}>Components</label>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: Math.round(totalWeight * 10) / 10 === 100 ? 'var(--green)' : 'var(--red)' }}>
                    Total: {totalWeight}%
                  </div>
                </div>

                {components.map((c, idx) => (
                  <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 'var(--rs)', padding: '.8rem', marginBottom: '.8rem', background: 'var(--surface-2)' }}>
                    <div style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', marginBottom: '.5rem' }}>
                      <input className="finput" placeholder="Component name" style={{ flex: 2 }} value={c.name} onChange={(e) => updateComponent(idx, 'name', e.target.value)} />
                      <input className="finput" placeholder="%" type="number" style={{ flex: 1 }} value={c.percentage} onChange={(e) => updateComponent(idx, 'percentage', e.target.value)} />
                      <button type="button" className="btn btn-danger btn-sm" title="Remove component" aria-label="Remove component" onClick={() => removeComponent(idx)}><i className="fa-solid fa-trash" /></button>
                    </div>

                    {c.children.map((ch, chIdx) => (
                      <div key={chIdx} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginLeft: '1.5rem', marginBottom: '.5rem' }}>
                        <i className="fa-solid fa-turn-up fa-rotate-90" style={{ color: 'var(--slate-lt)', fontSize: '.8rem' }} />
                        <input className="finput" placeholder="Sub-component name" style={{ flex: 2 }} value={ch.name} onChange={(e) => updateChild(idx, chIdx, 'name', e.target.value)} />
                        <input className="finput" placeholder="%" type="number" style={{ flex: 1 }} value={ch.percentage} onChange={(e) => updateChild(idx, chIdx, 'percentage', e.target.value)} />
                        <button type="button" className="btn btn-danger btn-sm" title="Remove sub-component" aria-label="Remove sub-component" onClick={() => removeChild(idx, chIdx)}><i className="fa-solid fa-trash" /></button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: '1.5rem' }} onClick={() => addChild(idx)}><i className="fa-solid fa-plus" /> Add Sub-component</button>
                  </div>
                ))}

                <button type="button" className="btn btn-outline btn-sm" onClick={addComponent}><i className="fa-solid fa-plus" /> Add Component</button>
              </>
            )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeModal}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || modalLoading}>
                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {submitting ? 'Saving…' : modalMode === 'create' ? 'Create Scheme' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {(viewData || viewLoading) && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewData(null)}>
          <div className="card modal" style={{ width: 560, maxWidth: '94vw' }} onClick={(e) => e.stopPropagation()}>
            {viewLoading && (
              <>
                <div className="modal-header">
                  <div className="modal-title">Scheme Details</div>
                  <button type="button" className="modal-close" onClick={() => setViewData(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="modal-body"><Loader label="Loading scheme…" /></div>
              </>
            )}
            {!viewLoading && viewData && (
              <>
                <div className="vw-hd">
                  <div className="vw-em"><i className="fa-solid fa-chart-pie" /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--ink)' }}>{viewData.name}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', gap: '.4rem' }}>
                    <span className={`pill ${viewData.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${viewData.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {viewData.is_active ? 'Active' : 'Inactive'}</span>
                    {viewData.locked && <span className="pill p-lock"><i className="fa-solid fa-lock" /> Locked</span>}
                  </div>
                  <button type="button" className="modal-close" onClick={() => setViewData(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="modal-body">
                  <div className="fl">Components</div>
                  {nestComponents(viewData.components).map((c) => (
                    <div key={c.id} style={{ border: '1px solid var(--border-soft)', borderRadius: 'var(--rs)', padding: '.6rem .8rem', marginTop: '.6rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span>{c.name}</span>
                        <span>{c.percentage}%{!c.is_active ? ' (Inactive)' : ''}</span>
                      </div>
                      {c.children.map((ch) => (
                        <div key={ch.id} style={{ display: 'flex', justifyContent: 'space-between', marginLeft: '1.2rem', color: 'var(--slate)', fontSize: 'var(--text-sm)', marginTop: '.3rem' }}>
                          <span><i className="fa-solid fa-turn-up fa-rotate-90" style={{ marginRight: '.3rem' }} />{ch.name}</span>
                          <span>{ch.percentage}%{!ch.is_active ? ' (Inactive)' : ''}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={() => setViewData(null)}>Close</button>
                  {perms?.update && !viewData.locked && (
                    <button type="button" className="btn btn-primary" onClick={() => { const row = viewData; setViewData(null); openEdit(row); }}><i className="fa-solid fa-pen" /> Edit Scheme</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Marking Scheme"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
